import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import { get as _get } from 'lodash';
import { combineLatest, of } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';

import {
  ChangeCoverageFeatureFacade,
  ChangeCoverageFeatureService
} from '@amfam/policy/change-coverage/feature';
import { PolicySelectors } from '@amfam/policy/data-access';
import { AutoPolicy, ChangeCoverageEmailType, ICoverage, Policy } from '@amfam/policy/models';
import {
  bindChangeCoverageQuote,
  VehicleCoveragesFacade
} from '@amfam/policy/vehicle-coverages/data-access';
import { userQuery } from '@amfam/shared/user';
import { Agent, agentQuery } from '@amfam/shared/utility/agent';
import { BrandSelectors } from '@amfam/shared/utility/brand';
import { fromRouterActions } from '@amfam/shared/utility/navigation';
import { LoadingSpinnerService } from '@amfam/ui-kit';

import {
  changeCoverageBindNotify,
  changeCoverageNotifyAgent,
  editCoveragesSendAnalytics,
  sendChangeCoverageAgentEmail,
  sendChangeCoverageAgentEmailFailed,
  sendChangeCoverageCustomerEmail,
  sendChangeCoverageCustomerEmailFailed,
  sendChangeCoverageCustomerEmailSuccess,
  vehicleCoverageBindQuote,
  vehicleCoveragesSendAnalytics
} from './change-coverage-feature.actions';

@Injectable()
export class ChangeCoverageFeaturesEffects {
  public isPartner: boolean;
  bindChangeCoveragesQuote$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(vehicleCoverageBindQuote),
      concatLatestFrom(() => this.store.select(PolicySelectors.selectAdvanceAutoPolicies)),
      switchMap(([action, autoPolicies]) => {
        const addVehicleBindPayload = action.payload;
        const selectedPolicyNumber = _get(
          (<AutoPolicy[]>autoPolicies).find(
            policy =>
              _get(policy, 'assignedProducer.producerIdNum') === addVehicleBindPayload.agent.id
          ),
          'policyNumber',
          ''
        );
        return [
          bindChangeCoverageQuote({
            payload: {
              workOrderNumber: addVehicleBindPayload.workOrderNumber,
              policyNumber: selectedPolicyNumber,
              vehicleId: addVehicleBindPayload.vehicleId,
              vin: null // TODO: Check if this effect is still being used
            }
          })
        ];
      })
    );
  });

  changeCoverageNotify$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(changeCoverageBindNotify, changeCoverageNotifyAgent),
      concatLatestFrom(action => [
        combineLatest([
          this.vehicleCoverageFacade.coverages$,
          this.changeCoverageFeatureFacade.initialCoverages$
        ]).pipe(
          map(([changedCoverages, initialCoverages]: [ICoverage[], ICoverage[]]) =>
            this.changeCoverageService.coverageReducer(changedCoverages, true, initialCoverages)
          ),
          map(this.changeCoverageService.getCovergesForEmail)
        ),
        this.store.select(PolicySelectors.selectVehicleDescription(action.payload.vehicleId)),
        this.vehicleCoverageFacade.workOrderNumberAndPolicyNumber$,
        this.store.select(userQuery.selectFirstName),
        this.store.select(userQuery.selectLastName),
        this.store.select(BrandSelectors.selectPartnerId),
        this.store.select(BrandSelectors.selectBrandId),
        this.store.select(BrandSelectors.selectAccountId),
        this.store.select(userQuery.selectCustomerId),
        this.store.select(userQuery.selectEmailAddress),
        this.store.select(BrandSelectors.selectCompany),
        this.store.select(PolicySelectors.selectSelectedPolicyNumber),
        this.changeCoverageFeatureFacade.initialCoverages$.pipe(
          map(c => this.changeCoverageService.coverageReducer(c, false)),
          map(this.changeCoverageService.getCovergesForEmail)
        )
      ]),
      switchMap(
        ([
          action,
          coverages,
          vehicleDescription,
          policyIdentifiers,
          firstname,
          lastname,
          partnerId,
          brandId,
          accountId,
          customerId,
          emailAddress,
          company,
          policyNumber,
          currentCoverages
        ]) => [
          sendChangeCoverageCustomerEmail({
            payload: this.changeCoverageService.prepareCustomerEmail(
              action.payload.type,
              partnerId,
              brandId,
              accountId,
              customerId,
              emailAddress,
              policyIdentifiers?.policyNumber || policyNumber,
              vehicleDescription || '',
              coverages || '',
              firstname || '',
              company,
              'CHANGE_COVERAGE',
              currentCoverages
            )
          }),
          sendChangeCoverageAgentEmail({
            payload: {
              policyNumber: policyIdentifiers?.policyNumber || policyNumber,
              customerName: (firstname || '') + ' ' + (lastname || ''),
              vehicleDescription: vehicleDescription || '',
              coverageChanges: coverages || '',
              workOrderNumber: policyIdentifiers?.workOrderNumber,
              emailType: action.payload.type
            },
            vehicleId: action.payload.vehicleId
          })
        ]
      )
    );
  });

  sendChangeCoverageAgentEmail$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(sendChangeCoverageAgentEmail),
      concatLatestFrom(() => [
        this.store.select(BrandSelectors.selectIsPartner),
        this.store.select(agentQuery.getAgents),
        this.store.select(PolicySelectors.selectAdvanceAutoPolicies)
      ]),
      switchMap(([action, isPartner, agents, policies]) => {
        const policy = (policies || []).find(
          (p: Policy) => p.policyNumber === action.payload.policyNumber?.replace(/-/g, '')
        );
        const producer = (agents || []).find(
          (agent: Agent) => agent.id === policy?.assignedProducer?.producerIdNum
        );
        return this.changeCoverageService
          .sendChangeCovergeLicensedAdvicerEmail(
            action.payload,
            action.vehicleId,
            isPartner,
            producer
          )
          .pipe(
            map(
              () => {
                this.spinnerService.stop();
                if (action.payload.emailType === ChangeCoverageEmailType.SEND_TO_AGENT) {
                  return fromRouterActions.Go({
                    path: [`policies/change-coverage/${action.vehicleId}/agent-help-confirmation`]
                  });
                } else {
                  return fromRouterActions.Go({
                    path: [`policies/change-coverage/${action.vehicleId}/confirmation`]
                  });
                }
              },
              catchError(() => of(sendChangeCoverageAgentEmailFailed()))
            )
          );
      })
    );
  });

  sendChangeCoverageCustomerEmail$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(sendChangeCoverageCustomerEmail),
      map(action => action.payload),
      switchMap(templatedEmail =>
        this.changeCoverageService.sendChangeCovergeCustomerEmail(templatedEmail).pipe(
          map(() => sendChangeCoverageCustomerEmailSuccess()),
          catchError(() => of(sendChangeCoverageCustomerEmailFailed()))
        )
      )
    );
  });

  analyticsNotification$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(vehicleCoveragesSendAnalytics, editCoveragesSendAnalytics),
        concatLatestFrom(() => this.store.select(BrandSelectors.selectIsPartner)),
        map(([action, isPartner]) => {
          if (isPartner) {
            return this.changeCoverageService.sendVehicleCoveragesAnalytics(action.payload);
          }
          return this.changeCoverageService.sendEditCoveragesAnalytics(action.payload);
        })
      );
    },
    { dispatch: false }
  );

  constructor(
    private actions$: Actions,
    private store: Store,
    private changeCoverageService: ChangeCoverageFeatureService,
    private vehicleCoverageFacade: VehicleCoveragesFacade,
    private changeCoverageFeatureFacade: ChangeCoverageFeatureFacade,
    private spinnerService: LoadingSpinnerService
  ) {}
}
